import { useServices } from 'containers/services';
import { useQuery, type UseQueryOptions, type UseQueryResult } from 'react-query';
import { QUERY_KEYS } from 'utils/queryKeys';
import type { GetMeterModelsArg, GetMeterModelsWithCountResponse } from '../../../lib/services';
import { useManufacturerFamily } from '../manufacturers/useManufacturerFamilyIds';

export type UseMeterModelsConfig = {
  includeManufacturerFamily?: boolean;
};

export const useMeterModels = (
  filter: GetMeterModelsArg['filter'] = {},
  options?: UseQueryOptions<GetMeterModelsWithCountResponse>,
  { includeManufacturerFamily = false }: UseMeterModelsConfig = {},
): UseQueryResult<GetMeterModelsWithCountResponse> => {
  const { meterModelsApiClient } = useServices();

  // Passing undefined keeps the manufacturers query disabled when the family isn't wanted.
  const family = useManufacturerFamily(includeManufacturerFamily ? filter.manufacturerId : undefined);

  // Never widen past the picked manufacturer: an empty family (none resolved) falls back to it alone.
  const ownIds = filter.manufacturerId ? [filter.manufacturerId] : undefined;
  const manufacturerIds = includeManufacturerFamily && family.ids?.length ? family.ids : ownIds;

  const effectiveFilter = { ...filter, manufacturerId: undefined, manufacturerIds };

  return useQuery({
    queryKey: QUERY_KEYS.meterModels.filter(effectiveFilter),
    queryFn: async ({ signal }) => await meterModelsApiClient.find({ filter: effectiveFilter, signal }),
    ...options,
    // Wait for the family, or the models are fetched twice: own first, then the family's.
    enabled: (options?.enabled ?? true) && !family.isLoading,
  });
};
